// ============================================================================
// THE ONE FILE TO EDIT PER RACE.
// Copy this file, change the values, point race.config.ts at it. No component,
// hook, or provider hardcodes anything below — retargeting is config-only.
// ============================================================================

import type { RaceConfig } from '../../types/config.ts';
import { DEFAULTS } from '../defaults.ts';

export const miSenDemPrimary2026: RaceConfig = {
  raceId: 'mi-sen-dem-primary-2026',
  title: 'Michigan U.S. Senate — Democratic Primary',
  subtitle: '2026 primary',
  seatContext:
    'Open seat (Sen. Gary Peters retiring). General opponent: Mike Rogers (R), unopposed.',
  lensNote:
    'A test of whether a progressive can win statewide in a purple state, and of ' +
    'how much heavy outside spending can actually buy. Pro-Stevens outside groups ' +
    'outspent pro-El-Sayed roughly ten to one.',
  electionDate: '2026-08-04',

  candidates: [
    {
      id: 'el-sayed',
      name: 'Abdul El-Sayed',
      shortName: 'El-Sayed',
      lane: 'Progressive',
      party: 'Democratic',
      color: '#2E7D6F',
      colorDark: '#3FB39D',
      pattern: 'solid',
      note: 'No-PAC pledge; backed by Sanders, Ocasio-Cortez.',
      // Real FEC candidate id (verified via the FEC candidate search). Live FEC
      // Schedule-E totals are shown alongside the reported seed figures below.
      fecCandidateIds: ['S6MI00418'],
      polymarketMarketSlug: '', // blank → "no market configured" (never fabricated)
      kalshiMarketTicker: '',
      apCandidateId: '',
    },
    {
      id: 'stevens',
      name: 'Haley Stevens',
      shortName: 'Stevens',
      lane: 'Establishment',
      party: 'Democratic',
      color: '#B4531F',
      colorDark: '#E07C3A',
      pattern: 'diagonal', // brief: "hatch"
      note: 'Backed by Whitmer, Peters, Schumer.',
      fecCandidateIds: ['S6MI00426'],
      polymarketMarketSlug: '',
      kalshiMarketTicker: '',
      apCandidateId: '',
    },
    {
      id: 'mcmorrow',
      name: 'Mallory McMorrow',
      shortName: 'McMorrow',
      lane: 'Withdrawn',
      party: 'Democratic',
      color: '#6B6B6B',
      colorDark: '#9AA1AC',
      pattern: 'dot', // brief: "dotted"
      note: 'Withdrew in July; remains on ballot.',
      withdrawn: true,
      fecCandidateIds: [],
      polymarketMarketSlug: '',
      kalshiMarketTicker: '',
      apCandidateId: '',
    },
  ],

  decisiveCounties: [
    { name: 'Wayne', why: 'Most populous county (Detroit). Reports late; skews toward El-Sayed.' },
    { name: 'Oakland', why: 'Large Detroit suburb; Stevens strength.' },
    { name: 'Macomb', why: 'Working-class Detroit suburb; Stevens target.' },
    { name: 'Washtenaw', why: 'Ann Arbor / U-Michigan; strong El-Sayed turf.' },
    { name: 'Kent', why: 'Grand Rapids; progressives tend to overperform here.' },
  ],

  refreshIntervalMs: DEFAULTS.refreshIntervalMs,
  financeRefreshEveryNTicks: DEFAULTS.financeRefreshEveryNTicks,
  stalenessThresholdMs: DEFAULTS.stalenessThresholdMs,
  divergenceFlagPct: DEFAULTS.divergenceFlagPct,
  snapshotCap: DEFAULTS.snapshotCap,

  voteProviders: [
    // PRIMARY live source: CivicAPI — free, no key, live statewide + per-county
    // results. Find a race id via
    //   https://civicapi.org/api/v2/race/search?province=MI&country=US
    // (match on election_name), then set civicRaceId here.
    {
      id: 'civicapi',
      label: 'CivicAPI',
      enabled: true,
      primary: true,
      transport: 'direct',
      civicRaceId: 84778, // "Michigan US Senate Democratic Primary"
    },
    // Manual override — always-available fallback. Enable to paste in your own
    // numbers (a second enabled source also activates multi-source reconciliation
    // + divergence flagging against CivicAPI).
    {
      id: 'manual',
      label: 'Manual entry',
      enabled: false,
      transport: 'direct',
      manualSource: '/manual/results.sample.json',
    },
    // AP — licensed feed, kept as a keyless stub. Flip `enabled` true and set the
    // key on Wrangler to switch it on with no other changes.
    {
      id: 'ap',
      label: 'AP',
      enabled: false,
      primary: false,
      transport: 'proxy',
      ap: { raceId: '', level: 'fips' },
    },
  ],

  marketProviders: [
    // Slugs/tickers left blank: if this primary has no prediction market, the
    // panel says so rather than inventing odds. Fill when a real market exists.
    { id: 'polymarket', label: 'Polymarket', enabled: true, transport: 'auto', eventSlug: 'michigan-democratic-senate-primary-winner' },
    // Kalshi now 403s browser-Origin requests, so go straight through the proxy
    // (which also rides on last-known-good odds when Kalshi rate-limits it).
    { id: 'kalshi', label: 'Kalshi', enabled: true, transport: 'proxy', eventTicker: 'kxsenatemid-26' },
  ],

  financeProviders: [{ id: 'fec', label: 'FEC', enabled: true, cycle: 2026 }],

  // Seed values from FEC-based reporting; the live FEC adapter supersedes these
  // when candidate ids are configured and the API is reachable. Clearly badged
  // "seed" in the UI when shown.
  financeSeed: {
    source: 'Seed values (from reporting)',
    sourceId: 'seed',
    fetchedAt: '2026-08-04T00:00:00.000Z',
    cycle: 2026,
    seeded: true,
    candidates: [
      {
        candidateId: 'stevens',
        supportTotal: 60_000_000,
        opposeTotal: 0,
        byGroup: [
          {
            committee: 'United Democracy Project (AIPAC-affiliated)',
            amount: 30_000_000,
            indicator: 'support',
          },
        ],
      },
      { candidateId: 'el-sayed', supportTotal: 4_600_000, opposeTotal: 0, byGroup: [] },
      { candidateId: 'mcmorrow', supportTotal: 0, opposeTotal: 0, byGroup: [] },
    ],
  },
};
